import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { deleteDialogCopy, holderPhrase, type RoleDeleteCheck } from '@/lib/roleLifecycle';
import { RoleLifecycleError, deleteAccountRole, fetchRoleDeleteCheck } from '@/services/roleLifecycleService';

/**
 * RBAC-3.2 · FR-4, FR-4b, FR-8. Delete a role the account owns.
 *
 * Opens on `role_delete_check()`, never on the holder count the list shows: a role with nobody on
 * it can still be held by an approval policy, a dashboard, an access setting or a pipeline. When
 * something holds it, the dialog names each thing and links to where it is set, and offers no
 * Delete. The database refuses the same delete (55006) if anything changed in between, and the
 * refusal is shown the same way.
 */
export interface DeleteRoleDialogProps {
  open: boolean;
  role: { id: number; permissionCount: number } | null;
  onOpenChange: (open: boolean) => void;
  onDeleted: (roleId: number) => void;
}

export function DeleteRoleDialog({ open, role, onOpenChange, onDeleted }: DeleteRoleDialogProps) {
  const [check, setCheck] = useState<RoleDeleteCheck | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      setCheck(await fetchRoleDeleteCheck(id));
    } catch (e) {
      setCheck(null);
      setError(e instanceof Error ? e.message : 'The role could not be checked.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Keyed on the id: the parent builds `role` inline, so the object is new on every render.
  const roleId = role?.id ?? null;
  useEffect(() => {
    if (!open || roleId === null) return;
    setCheck(null);
    void load(roleId);
  }, [open, roleId, load]);

  const confirm = async () => {
    if (!check || !role) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteAccountRole(check.roleId);
      onDeleted(check.roleId);
      onOpenChange(false);
    } catch (e) {
      if (e instanceof RoleLifecycleError && e.code === '55006' && e.holders.length > 0) {
        // Something took hold of the role since the check. Show what, the same way.
        setCheck({ ...check, deletable: false, holders: e.holders, summary: null });
      } else {
        setError(e instanceof Error ? e.message : 'The role could not be deleted.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const copy = check && role ? deleteDialogCopy(check, role.permissionCount) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !deleting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{copy?.title ?? 'Delete role'}</DialogTitle>
          {copy && <DialogDescription>{copy.lead}</DialogDescription>}
        </DialogHeader>

        {loading && !check && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking what uses this role…
          </div>
        )}

        {check && check.holders.length > 0 && (
          <ul className="divide-y divide-border rounded-md border border-border" aria-label="In use by">
            {check.holders.map((h) => (
              <li key={h.noun} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="text-foreground">{holderPhrase(h)}</span>
                {h.href ? (
                  <Link to={h.href} className="shrink-0 text-primary underline-offset-2 hover:underline">
                    Open
                  </Link>
                ) : (
                  <span className="shrink-0 text-xs text-muted-foreground">No screen yet. Ask Joules support</span>
                )}
              </li>
            ))}
          </ul>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleting}>
            {/* Not "Close": the dialog's ✕ already carries that name, and two would read the same. */}
            {copy?.confirmLabel ? 'Cancel' : 'Done'}
          </Button>
          {copy?.confirmLabel && (
            <Button variant="destructive" onClick={confirm} disabled={deleting}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {copy.confirmLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteRoleDialog;
